import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import { Gem, ChevronLeft, LogOut, UserCircle, Moon, Sun } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import PhoneVerificationModal from '../../auth/components/PhoneVerificationModal';
import { getUser, logout } from '../../state/authSlice';
import { getCourierSetting, getIsWhatsappConnected } from '../../state/courierSlice';
import { useRewards } from '../../hooks/useRewards';
import { useDarkMode } from '../../context/DarkModeContext';
import { shouldPromptPhoneVerification } from '../../utils/verificationPrompt';

const DashboardLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isVerificationModalOpen, setVerificationModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { isDarkMode, setIsDarkMode } = useDarkMode();
  const user = useSelector(getUser);
  const courierSetting = useSelector(getCourierSetting);
  const isWhatsappConnected = useSelector(getIsWhatsappConnected);
  const { rewardsAccount } = useRewards();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mainRef = React.useRef<HTMLElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  
  // Check if user has dismissed the verification prompt
  const hasUserDismissedVerification = () => {
    const dismissed = localStorage.getItem('phoneVerificationDismissed');
    return dismissed === 'true';
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/auth');
  };

  const handleCloseVerificationModal = () => {
    setVerificationModalOpen(false);
    // Remember that user dismissed the verification
    localStorage.setItem('phoneVerificationDismissed', 'true');
  };

  useEffect(() => {
    // Only prompt when a usable verification channel exists — SMS, or WhatsApp
    // that is actually connected. Never nag for a WhatsApp code the courier's
    // disconnected session can't deliver.
    if (
      user &&
      shouldPromptPhoneVerification({
        phoneVerified: user.phoneVerified,
        requireVerification: courierSetting?.requireVerification,
        isSmsApiEnabled: courierSetting?.isSmsApiEnabled,
        useWaApi: courierSetting?.useWaApi,
        isWhatsappConnected,
        dismissed: hasUserDismissedVerification(),
      })
    ) {
      setVerificationModalOpen(true);
    }
  }, [user, courierSetting, isWhatsappConnected]);

  // Clear the dismissal flag when user successfully verifies their phone
  useEffect(() => {
    if (user?.phoneVerified) {
      localStorage.removeItem('phoneVerificationDismissed');
    }
  }, [user?.phoneVerified]);

  // Scroll to top when location changes
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // Get page title based on current path
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/mailbox')) return 'Mailbox';
    if (path.includes('/packages')) {
      if (path.includes('/packages/')) return 'Package Details';
      return 'Packages';
    }
    if (path.includes('/delivery-request')) return 'Delivery Request';
    if (path.includes('/authorized-users')) return 'Authorized Users';
    if (path.includes('/calculator')) return 'Calculator';
    if (path.includes('/invoices')) {
      if (path.includes('/invoices/')) return 'Invoice Details';
      return 'Invoices';
    }
    if (path.includes('/shop-for-me')) return 'Shop For Me';
    if (path.includes('/shop')) return 'Shop';
    if (path.includes('/referrals')) return 'Referrals & Rewards';
    if (path.includes('/profile')) return 'Profile';
    if (path.includes('/warehouse')) return user?.isCourier ? 'Dashboard' : 'Warehouse';
    if (path.includes('/more')) return 'More';
    if (path.includes('/report-problem')) return 'Report Problem';
    if (path.includes('/contact-us')) return 'Contact Us';
    return 'Dashboard';
  };
  // Check if current path is a main page or a sub-page
  const navItems = ['/packages', '/mailbox', '/delivery-request', '/calculator', '/invoices', '/warehouse', '/shop-for-me', '/shop', '/more'];
  const isMainPage = navItems.includes(location.pathname);
  // Handle back navigation
  const handleBack = () => {
    // If on a sub-page of packages or invoices, go back to the main page
    if (location.pathname.includes('/packages/')) {
      navigate('/packages');
    } else if (location.pathname.includes('/invoices/')) {
      navigate('/invoices');
    } else if (location.pathname === '/profile' || location.pathname === '/referrals' || location.pathname === '/report-problem' || location.pathname === '/contact-us') {
      navigate('/more');
    } else {
      // Default fallback
      navigate(-1);
    }
  };
  return <div className={`flex h-screen ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      {/* Render the PhoneVerificationModal */}
      <PhoneVerificationModal
        isOpen={isVerificationModalOpen}
        onClose={handleCloseVerificationModal}
      />
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
      </div>
      {/* Main Content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Header */}
        <header className={`${isDarkMode? 'bg-gray-800': 'bg-primary'} text-white shadow-md`}>
          <div className="flex items-center justify-between p-4">
            {/* Back button (visible only on sub-pages and on mobile) */}
            <div className="flex items-center">
              {!isMainPage && <button onClick={handleBack} className="text-white mr-3 hover:bg-gray-700 p-1 rounded-full md:hidden">
                  <ChevronLeft className="h-5 w-5" />
                </button>}
              {/* Page Title */}
              <div className="font-semibold text-lg">{getPageTitle()}</div>
            </div>
            {/* User info and gems */}
            <div className="flex items-center">
              <div className="flex flex-col items-end mr-3">
                <span className="text-sm">Hi {user?.firstName || 'Guest'}</span>
                <div className="flex items-center text-xs">
                  <Gem className="h-3 w-3 mr-1" />
                  <span>{rewardsAccount?.points || 0} Gems</span>
                </div>
              </div>
              <div ref={menuRef} className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="h-9 w-9 rounded-full bg-white text-gray-800 flex items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <span>{user?.firstName?.[0]}{user?.lastName?.[0] || user?.firstName?.[1] || '?'}</span>
                </button>

                {/* User Menu Popover */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setIsUserMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <UserCircle className="h-4 w-4 mr-2" />
                        Profile
                      </button>
                      <button
                        onClick={() => {
                          setIsDarkMode(!isDarkMode);
                          setIsUserMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        {isDarkMode ? (
                          <Sun className="h-4 w-4 mr-2" />
                        ) : (
                          <Moon className="h-4 w-4 mr-2" />
                        )}
                        {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                      </button>
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsUserMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 border-t border-gray-200 dark:border-gray-700"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        {/* Page Content */}
        <main ref={mainRef} className={`flex-1 overflow-y-auto ${isDarkMode ? 'dark:bg-gray-900 dark:text-white' : 'bg-gray-50'}`}>
          <Outlet />
        </main>
        {/* Mobile Navigation */}
        <div className="md:hidden">
          <MobileNav />
        </div>
      </div>
    </div>;
};
export default DashboardLayout;
